import { createContext, useContext } from "react";
import type {
    ScannerHandlerEntry,
    ScannerHintUiState,
    ScannerRegistrationConfig,
    ScannerScanTarget,
    ScannerStatus,
} from "./types";

/** Обновление пользовательского состояния активной scanner-подсказки. */
export type ScannerHintUiStateUpdate = ScannerHintUiState | ((currentState: ScannerHintUiState) => ScannerHintUiState);

/** Handle зарегистрированного обработчика сканирования. */
export interface ScannerRegistrationHandle {
    /** Уникальный идентификатор регистрации. */
    id: string;
    /** Снимает регистрацию обработчика. */
    unregister: () => void;
}

/** Значение scanner context. */
export interface ScannerContextValue {
    /** Текущее состояние активного сценария сканирования. */
    status: ScannerStatus;
    /** Пользовательское состояние активной scanner-подсказки. */
    hintUiState?: ScannerHintUiState;
    /**
     * Регистрирует обработчик сканирования.
     * @param config параметры обработчика
     * @returns handle регистрации
     */
    register: (config: ScannerRegistrationConfig) => ScannerRegistrationHandle;
    /**
     * Обновляет ранее зарегистрированный обработчик.
     * @param id идентификатор регистрации
     * @param config новые параметры обработчика
     */
    update: (id: string, config: ScannerRegistrationConfig) => void;
    /**
     * Обновляет пользовательское состояние активной scanner-подсказки.
     * @param update новое состояние или updater от текущего состояния
     */
    updateActiveHintUiState: (update: ScannerHintUiStateUpdate) => void;
    /**
     * Обновляет пользовательское состояние scanner-подсказки по стабильному ключу.
     * @param hintStateKey ключ состояния scanner-подсказки
     * @param defaultState состояние, если ключ ещё не был записан
     * @param update новое состояние или updater от текущего состояния
     */
    updateHintUiState: (hintStateKey: string, defaultState: ScannerHintUiState, update: ScannerHintUiStateUpdate) => void;
    /**
     * Включает глобальную паузу маршрутизации сканов.
     *
     * @internal Используется только {@link ScannerOverlaySuppressBridge} из `AppProviders`.
     * @param suppressed `true` — keyboard capture и активный handler временно отключены
     */
    setGlobalSuppress: (suppressed: boolean) => void;
    /**
     * Захватывает текущий active handler как цель отложенного inject. \
     * Вызывать **до** открытия blocking overlay: после suppress active entry станет `undefined`.
     *
     * `inject` обходит `paused` (ручной ввод — альтернатива скану, а не поверх него), \
     * но не обходит `busy` и unregister.
     * @returns target с `inject` по id регистрации, или `null` если нет active entry
     */
    captureActiveScanTarget: () => ScannerScanTarget | null;
}

/** Внутреннее состояние scanner registry. */
export interface ScannerRegistryState {
    /** Зарегистрированные обработчики. */
    entries: ScannerHandlerEntry[];
    /** Признак обработки предыдущего скана. */
    busy: boolean;
}

export const ScannerContext = createContext<ScannerContextValue | undefined>(undefined);

/**
 * Возвращает API app-level scanner registry.
 * @throws Error если вызван вне `ScannerProvider`
 */
export function useScannerContext(): ScannerContextValue {
    const ctx = useContext(ScannerContext);
    if (ctx == null) {
        throw new Error("useScannerContext must be used within ScannerProvider");
    }
    return ctx;
}
